import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  Icon,
  Spinner,
  Switch,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { FaDownload, FaSyncAlt } from 'react-icons/fa';
import PropTypes from 'prop-types';
import CommonTable from '../../../components/commonTable/CommonTable';
import CommonPagination from '../../../components/commonPagination/CommonPagination';
import {
  GetInvoiceJobs,
  DownloadInvoiceJobFile,
} from 'reactRedux/thunks/settlementsThunk/SettlementsThunk';

// Auto-refresh cadence while the Invoice Jobs tab is open (RAT-1810).
const JOB_POLL_INTERVAL_MS = 5000;

// Statuses that can still change on their own, so the visible page is worth
// re-fetching. Once no row on the current page is active, polling stops
// (RAT-1827).
const ACTIVE_JOB_STATUSES = ['pending', 'in_progress'];

const OPERATION_LABEL = {
  merchant_download: 'Download',
  merchant_email: 'Email',
  merchant_generate: 'Generate',
  // RAT-1826: preview (PV-) invoice download - not an issued invoice.
  merchant_preview_download: 'Preview',
};

// Maps a job to its status chip: label + Chakra colorScheme + optional tooltip.
const getStatusDisplay = (job) => {
  switch (job?.status) {
    case 'pending':
      return { label: 'Queued', colorScheme: 'gray' };
    case 'in_progress':
      return {
        label: `Processing ${job?.done ?? 0}/${job?.total ?? 0}`,
        colorScheme: 'orange',
      };
    case 'completed':
      if (job?.operation === 'merchant_email') {
        return { label: 'Sent', colorScheme: 'green' };
      }
      if (job?.operation === 'merchant_generate') {
        return { label: 'Generated', colorScheme: 'green' };
      }
      return { label: 'Ready', colorScheme: 'green' };
    case 'failed':
      return {
        label: 'Failed',
        colorScheme: 'red',
        tooltip: job?.firstError || 'Job failed',
      };
    default:
      return { label: job?.status || 'Unknown', colorScheme: 'gray' };
  }
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const InvoiceJobsList = ({ onGenerateClick, refreshKey }) => {
  const dispatch = useDispatch();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const { invoiceJobs, invoiceJobsTotal, invoiceJobsLoading } = useSelector(
    (state) => state.SettlementsSlice,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [mineOnly, setMineOnly] = useState(false);

  const fetchJobs = useCallback(
    (silent = false) => {
      dispatch(GetInvoiceJobs({ page, limit, mine: mineOnly }, silent));
    },
    [dispatch, page, limit, mineOnly],
  );

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Only the rows currently on screen decide whether polling is needed, so
  // switching pages re-evaluates it: a page holding a queued/processing job
  // starts the background refresh, a page of finished jobs runs no timer.
  const hasActiveJobs = (invoiceJobs || []).some((job) =>
    ACTIVE_JOB_STATUSES.includes(job?.status),
  );

  // Background poll: refreshes the rows in place (no loader) and clears itself
  // as soon as the last active job on this page reaches a final status. The
  // component only mounts while the Invoice Jobs tab is active, so polling also
  // stops the moment the user leaves the tab.
  useEffect(() => {
    if (!hasActiveJobs) return undefined;
    const timer = setInterval(() => fetchJobs(true), JOB_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActiveJobs, fetchJobs]);

  // A job enqueued while this tab is already open would otherwise stay hidden
  // until the next manual refresh, since polling is off when nothing is active.
  // The new job sits at the top of the list, so go back to the first page.
  const lastRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;
    if (page === 1) {
      fetchJobs(true);
    } else {
      setPage(1);
    }
  }, [refreshKey, page, fetchJobs]);

  const handleDownload = useCallback(
    (job) => {
      const isZip = (job?.merchantCount ?? 0) > 1;
      dispatch(
        DownloadInvoiceJobFile(
          job.queueId,
          `invoice-job-${job.queueId}`,
          isZip,
        ),
      );
    },
    [dispatch],
  );

  const columnHelper = createColumnHelper();
  const headerText = (label) => (
    <Text
      align="left"
      fontSize={{ sm: '10px', lg: '12px' }}
      color="gray.400"
      whiteSpace="nowrap"
    >
      {label}
    </Text>
  );
  const cellText = (value) => (
    <Text
      color={textColor}
      fontSize="sm"
      fontWeight="600"
      align="left"
      whiteSpace="nowrap"
    >
      {value}
    </Text>
  );

  // Plain array (not memoized) to match the MerchantList columns pattern.
  const columns = [
    columnHelper.accessor((row) => row?.periodLabel, {
      id: 'periodLabel',
      header: () => headerText('Period'),
      cell: (info) => cellText(info.getValue() || 'N/A'),
    }),
    columnHelper.accessor((row) => row?.merchantLabel, {
      id: 'merchant',
      header: () => headerText('Merchant(s)'),
      cell: (info) =>
        cellText(info.getValue() || `${info.row.original?.merchantCount ?? 0}`),
    }),
    columnHelper.accessor((row) => row?.operation, {
      id: 'operation',
      header: () => headerText('Type'),
      cell: (info) => cellText(OPERATION_LABEL[info.getValue()] || 'N/A'),
    }),
    columnHelper.accessor((row) => row?.requestedByName, {
      id: 'requestedByName',
      header: () => headerText('Requested by'),
      cell: (info) => cellText(info.getValue() || 'N/A'),
    }),
    columnHelper.accessor((row) => row?.status, {
      id: 'status',
      header: () => headerText('Status'),
      cell: (info) => {
        const display = getStatusDisplay(info.row.original);
        const badge = (
          <Badge
            colorScheme={display.colorScheme}
            borderRadius="6px"
            px="8px"
            py="2px"
            fontSize="xs"
            textTransform="none"
          >
            {display.label}
          </Badge>
        );
        return display.tooltip ? (
          <Tooltip label={display.tooltip} hasArrow>
            {badge}
          </Tooltip>
        ) : (
          badge
        );
      },
    }),
    columnHelper.accessor((row) => row?.createdAt, {
      id: 'createdAt',
      header: () => headerText('Created'),
      cell: (info) => cellText(formatDateTime(info.getValue())),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => headerText('Action'),
      cell: (info) => {
        const job = info.row.original;
        if (!job?.downloadable) {
          return (
            <Text color="gray.400" fontSize="sm">
              -
            </Text>
          );
        }
        return (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="blue"
            leftIcon={<Icon as={FaDownload} w="12px" h="12px" />}
            onClick={() => handleDownload(job)}
            className="no-navigate"
          >
            Download
          </Button>
        );
      },
    }),
  ];

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        wrap="wrap"
        gap={3}
        mb={3}
        mt={2}
      >
        <Flex align="center" gap={2}>
          <Text fontSize="lg" fontWeight="700" color={textColor}>
            Invoice Jobs
          </Text>
          {invoiceJobsLoading && <Spinner size="sm" color="orange.400" />}
        </Flex>
        <Flex align="center" gap={4}>
          <Flex align="center" gap={2}>
            <FormLabel htmlFor="mine-only" mb="0" fontSize="sm">
              My jobs only
            </FormLabel>
            <Switch
              id="mine-only"
              isChecked={mineOnly}
              onChange={(e) => {
                setPage(1);
                setMineOnly(e.target.checked);
              }}
              colorScheme="orange"
            />
          </Flex>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FaSyncAlt} w="12px" h="12px" />}
            onClick={() => fetchJobs()}
          >
            Refresh
          </Button>
          {typeof onGenerateClick === 'function' && (
            <Button
              size="sm"
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              onClick={onGenerateClick}
            >
              Generate Invoices
            </Button>
          )}
        </Flex>
      </Flex>

      <CommonTable
        columns={columns}
        data={invoiceJobs || []}
        isLoading={invoiceJobsLoading}
        keyValue="queueId"
      />

      <CommonPagination
        totalCount={invoiceJobsTotal}
        activePage={page}
        pageCount={Math.ceil((invoiceJobsTotal || 0) / limit)}
        onPageChange={setPage}
        itemsPerPage={limit}
        onItemsPerPageChange={(newLimit) => {
          setPage(1);
          setLimit(newLimit);
        }}
      />
    </Box>
  );
};

InvoiceJobsList.propTypes = {
  onGenerateClick: PropTypes.func,
  refreshKey: PropTypes.number,
};

export default InvoiceJobsList;
